// 38, -96 is around the center of the continental US
export function findCenterCoordinate(coords: number[][]) {
	if (!coords || !coords.length) {
		return { lat: 38, lng: -96 };
	}

	const filtered = coords.filter((item) => item[0] && item[1]);

	const x = filtered.map((item) => item[0] as number);
	const y = filtered.map((item) => item[1] as number);

	const minX = Math.min(...x);
	const maxX = Math.max(...x);
	const minY = Math.min(...y);
	const maxY = Math.max(...y);

	if (minX === Infinity || maxX === -Infinity || minY === Infinity || maxY === -Infinity) {
		return { lat: 38, lng: -96 };
	}

	return {
		lat: (minX + maxX) / 2,
		lng: (minY + maxY) / 2,
		bounds: {
			north: maxY,
			south: minY,
			east: maxX,
			west: minX,
		},
	};
}

export function getDistanceInMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
	const R = 3959; // Earth's radius in miles
	const dLat = (lat2 - lat1) * (Math.PI / 180);
	const dLon = (lon2 - lon1) * (Math.PI / 180);
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
}

// Usage example to filter an array of positions:
/**
 * @param centerLat
 * @param centerLon
 * @param positions (must have lat and lon properties)
 * @param radiusMiles
 */
export function filterPositionsWithinRadius(
	centerLat: number,
	centerLon: number,
	positions: any[],
	radiusMiles: number = 50,
) {
	return positions.filter((pos) => getDistanceInMiles(centerLat, centerLon, pos.lat, pos.lon) <= radiusMiles);
}

/**
 * Filters positions within a square bounding box. Faster but less accurate than radius filtering.
 * Uses approximately 69 miles per degree latitude and varies by latitude for longitude.
 * @param centerLat Center latitude
 * @param centerLon Center longitude
 * @param positions Array of positions with lat/lon properties
 * @param squareSideMiles Length of the square's side in miles (equivalent to diameter)
 */
export function filterPositionsWithinSquare(
	centerLat: number,
	centerLon: number,
	positions: any[],
	squareSideMiles: number = 50,
) {
	// Convert miles to degrees
	const latDegrees = squareSideMiles / 69; // ~69 miles per degree of latitude
	const lonDegrees = squareSideMiles / (Math.cos(centerLat * (Math.PI / 180)) * 69);

	const latMin = centerLat - latDegrees;
	const latMax = centerLat + latDegrees;
	const lonMin = centerLon - lonDegrees;
	const lonMax = centerLon + lonDegrees;

	return positions.filter((pos) => pos.lat >= latMin && pos.lat <= latMax && pos.lon >= lonMin && pos.lon <= lonMax);
}
